import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { CheckCircle2, ChevronRight, ExternalLink, SlidersHorizontal } from 'lucide-react';
import { listLeads, type Lead } from '../lib/leadsApi';
import { useArchiveLead, useLead, useRestoreLead, useUpdateLeadById } from '../hooks/useLeads';
import { ToastProvider } from '../components/tasks/linear/Toast';
import { LeadMarkdownEditor } from '../components/leads/detail/LeadMarkdownEditor';
import { LeadActions } from '../components/leads/detail/LeadActions';
import { LeadTaskSuggestions } from '../components/leads/detail/LeadTaskSuggestions';
import { LeadMeetingNotesSection } from '../components/leads/detail/LeadMeetingNotesSection';
import { LeadMeetingsSection } from '../components/leads/detail/LeadMeetingsSection';
import { LeadDetailActivity } from '../components/leads/detail/LeadDetailActivity';
import { LeadDetailSidebar } from '../components/leads/detail/LeadDetailSidebar';
import { ConvertToProjectDialog } from '../components/leads/detail/ConvertToProjectDialog';
import {
  LeadStageEditor,
  LeadTemperatureEditor,
} from '../components/leads/detail/leadInlineEditors';
import { BottomSheet } from '../components/ui/BottomSheet';

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/**
 * CLE-192 — dedicated Lead Detail page (spec 3.3).
 *
 * Two-column dark layout matching Task Detail (CLE-182/CLE-184): the left
 * column holds the breadcrumb, an inline-editable title, a markdown
 * description, the Actions section and the unified activity + comments
 * timeline; the right column is the ~290px properties sidebar with the Convert
 * to Project and Archive actions. Below lg that column would stack under the
 * page and squeeze the scrolling column to a sliver, hiding the description, so
 * it opens as a sheet instead, with the stage and temperature kept up top like
 * on other pipeline apps. The `:id` route param may be a UUID (used
 * directly) or a human identifier like "LEAD-123" (resolved via list search,
 * since GET /api/leads/:id resolves by UUID only).
 */
export function LeadDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const isUuid = id ? UUID_RE.test(id) : false;
  const { data: resolved } = useQuery({
    queryKey: ['lead-by-identifier', id],
    queryFn: async () => {
      const res = await listLeads({ search: id, pageSize: 25 });
      const exact = res.leads.find((l) => l.identifier?.toLowerCase() === id?.toLowerCase());
      return exact ?? res.leads[0] ?? null;
    },
    enabled: Boolean(id) && !isUuid,
  });

  const leadId = isUuid ? id : resolved?.id;
  const { data: lead, isLoading, isError } = useLead(leadId ?? undefined);

  function goBack() {
    if (window.history.length > 1) navigate(-1);
    else navigate('/leads');
  }

  const stillResolving = !isUuid && Boolean(id) && resolved === undefined;
  if (isLoading || stillResolving) {
    return <div className="bg-gray-50 p-8 text-sm text-gray-400">Se încarcă lead-ul…</div>;
  }
  if (isError || !lead || (!isUuid && resolved === null)) {
    return (
      <div className="bg-gray-50 p-8">
        <p className="text-sm text-red-500">Lead-ul nu a fost găsit.</p>
        <button type="button" onClick={goBack} className="mt-2 text-sm text-brand-600 underline">
          Înapoi la leaduri
        </button>
      </div>
    );
  }

  return (
    <ToastProvider>
      <LeadDetail lead={lead} onBack={goBack} onNavigate={(to) => navigate(to)} />
    </ToastProvider>
  );
}

function LeadDetail({
  lead,
  onBack,
  onNavigate,
}: {
  lead: Lead;
  onBack: () => void;
  onNavigate: (to: string) => void;
}) {
  const update = useUpdateLeadById();
  const archive = useArchiveLead();
  // An archived lead stays on its page after restore: it is back in the list.
  const restore = useRestoreLead();
  const [converting, setConverting] = useState(false);
  const [propertiesOpen, setPropertiesOpen] = useState(false);

  const [title, setTitle] = useState(lead.title);
  useEffect(() => setTitle(lead.title), [lead.title]);

  // A long title wraps on a phone; one fixed row would cut it off.
  const titleRef = useRef<HTMLTextAreaElement>(null);
  useLayoutEffect(() => {
    const el = titleRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [title]);

  function commitTitle() {
    const trimmed = title.trim();
    if (!trimmed || trimmed === lead.title) {
      setTitle(lead.title);
      return;
    }
    update.mutate({ id: lead.id, title: trimmed });
  }

  function handleArchive() {
    archive.mutate(lead.id, { onSuccess: () => onBack() });
  }

  return (
    <div className="flex h-full min-h-0 flex-col bg-gray-50 text-gray-800 lg:flex-row">
      <main className="min-w-0 flex-1 overflow-y-auto px-5 py-6 sm:px-8 lg:px-10">
        <div className="mx-auto max-w-3xl">
          {/* Breadcrumb: Leads > Pipeline > LEAD-XXX */}
          <nav className="flex items-center gap-1 text-[13px] text-gray-500">
            <button type="button" onClick={onBack} className="hover:text-gray-800">
              Leaduri
            </button>
            <ChevronRight size={14} className="text-gray-300" />
            <span>{lead.pipeline?.name ?? 'Pipeline'}</span>
            <ChevronRight size={14} className="text-gray-300" />
            <span className="font-mono text-gray-700">{lead.identifier}</span>
          </nav>

          {/* Converted banner */}
          {lead.convertedToProjectId && (
            <Link
              to={`/projects/${lead.convertedToProjectId}`}
              className="mt-4 flex items-center justify-between gap-2 rounded-md border border-green-500/40 bg-green-500/10 px-3 py-2 text-[13px] text-green-600 hover:bg-green-500/20"
            >
              <span className="flex items-center gap-2">
                <CheckCircle2 size={15} /> Convertit în proiect
              </span>
              <ExternalLink size={14} />
            </Link>
          )}

          {/* Editable title */}
          <textarea
            ref={titleRef}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            onBlur={commitTitle}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                (e.target as HTMLTextAreaElement).blur();
              }
              if (e.key === 'Escape') {
                setTitle(lead.title);
                (e.target as HTMLTextAreaElement).blur();
              }
            }}
            rows={1}
            className="mt-4 w-full resize-none bg-transparent text-[22px] font-semibold leading-tight text-gray-900 placeholder:text-gray-400 focus:outline-none"
            placeholder="Titlul lead-ului"
          />

          <div className="mt-2 flex flex-wrap items-center gap-2 lg:hidden">
            <LeadStageEditor lead={lead} />
            <LeadTemperatureEditor lead={lead} />
            <button
              type="button"
              onClick={() => setPropertiesOpen(true)}
              className="ml-auto inline-flex items-center gap-1.5 rounded-md border border-gray-200 px-2.5 py-1.5 text-[13px] font-medium text-gray-700 hover:bg-gray-100"
            >
              <SlidersHorizontal size={14} /> Proprietăți
            </button>
          </div>

          {/* Description */}
          <div className="mt-2">
            <LeadMarkdownEditor
              leadId={lead.id}
              value={lead.description ?? ''}
              isSaving={update.isPending}
              onSave={(description) => update.mutate({ id: lead.id, description })}
            />
          </div>

          {/* AI-proposed actions, decided by hand — then the actions themselves */}
          <LeadTaskSuggestions leadId={lead.id} />
          <LeadActions leadId={lead.id} />

          {/* Logged meetings (Întâlniri) linked to this lead */}
          <LeadMeetingsSection leadId={lead.id} />

          {/* Meeting notes & interactions */}
          <LeadMeetingNotesSection leadId={lead.id} />

          {/* Activity timeline + comments */}
          <LeadDetailActivity leadId={lead.id} />
        </div>
      </main>

      <LeadDetailSidebar
        lead={lead}
        onConvert={() => setConverting(true)}
        onArchive={handleArchive}
        isArchiving={archive.isPending}
        onRestore={() => restore.mutate(lead.id)}
        isRestoring={restore.isPending}
      />

      <BottomSheet
        open={propertiesOpen}
        onClose={() => setPropertiesOpen(false)}
        ariaLabel="Proprietăți"
      >
        {/* The sheet caps its height; without this the Archive button is out of reach. */}
        <div className="min-h-0 overflow-y-auto">
          <LeadDetailSidebar
            inSheet
            lead={lead}
            onConvert={() => {
              setPropertiesOpen(false);
              setConverting(true);
            }}
            onArchive={handleArchive}
            isArchiving={archive.isPending}
            onRestore={() => restore.mutate(lead.id)}
            isRestoring={restore.isPending}
          />
        </div>
      </BottomSheet>

      {converting && (
        <ConvertToProjectDialog
          lead={lead}
          onClose={() => setConverting(false)}
          onConverted={(projectId) => onNavigate(`/projects/${projectId}`)}
        />
      )}
    </div>
  );
}
